import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import { useDispatch } from "react-redux";

import { showToast, hideToast } from "app/actions/toast_actions";
import { useQuotaQueryClient } from "app/pages/sales-intelligence/modules/quota/hooks/useQuotaQueryClient";
import salesApiService from "app/pages/sales-intelligence/services/salesApiService";
import { exportToExcelToastThunk } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/effects";
import { exportWebsitesToExcelService } from "app/pages/sales-intelligence/sub-modules/table-actions-panel/services/exportExcelService";

export const useDownloadCompetitorsExcelMutation = (): UseMutationResult<
    void,
    void,
    { domain: string; competitors: string[] }
> => {
    const dispatch = useDispatch();
    const quotaQueryClient = useQuotaQueryClient();

    return useMutation({
        async mutationFn({ domain, competitors }): Promise<void> {
            const { createToast, getExportingToastTitle, getSuccessToastTitle, getErrorToastTitle, getToastComponent } =
                exportWebsitesToExcelService;

            const toast = createToast(getExportingToastTitle(competitors.length));

            dispatch(showToast(getToastComponent(toast.children as string), "success", toast.timestamp));

            if (domain && competitors.length > 0) {
                const result = await salesApiService.companyAnalysis.downloadCompetitorsExcel({ domain, competitors });
                dispatch(hideToast(toast));

                if (result.success) {
                    dispatch(exportToExcelToastThunk(getSuccessToastTitle()));
                    await quotaQueryClient.invalidateExportQuota();
                    return;
                }

                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
                throw new Error(result.error);
            } else {
                dispatch(hideToast(toast));
                dispatch(exportToExcelToastThunk(getErrorToastTitle()));
                throw new Error("No domains.");
            }
        },
    });
};
